import React, { useState, useRef } from 'react';
import type { Contract, DigitalSignatureData } from '../../types/contracts';

interface DigitalSignatureModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSign: (signatureData: DigitalSignatureData) => void;
  contract: Contract;
}

const DigitalSignatureModal: React.FC<DigitalSignatureModalProps> = ({
  isOpen,
  onClose,
  onSign,
  contract,
}) => {
  const [step, setStep] = useState<'preview' | 'sign' | 'confirm'>('preview');
  const [signatureData, setSignatureData] = useState<string>('');
  const [consentConfirmed, setConsentConfirmed] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  if (!isOpen) return null;

  const getBrowserFingerprint = () => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx!.textBaseline = 'top';
    ctx!.font = '14px Arial';
    ctx!.fillText('Browser fingerprint', 2, 2);
    return canvas.toDataURL();
  };

  const getCurrentLocation = (): Promise<
    { latitude: number; longitude: number; accuracy: number } | undefined
  > => {
    return new Promise(resolve => {
      if (!navigator.geolocation) {
        resolve(undefined);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        position => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
          });
        },
        () => resolve(undefined),
        { timeout: 10000 }
      );
    });
  };

  const handleCanvasMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    setIsDrawing(true);
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const ctx = canvas.getContext('2d')!;
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const handleCanvasMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;

    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const ctx = canvas.getContext('2d')!;
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#000';
    ctx.lineTo(x, y);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const handleCanvasMouseUp = () => {
    setIsDrawing(false);
    const canvas = canvasRef.current!;
    setSignatureData(canvas.toDataURL());
  };

  const clearSignature = () => {
    const canvas = canvasRef.current!;
    const ctx = canvas.getContext('2d')!;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setSignatureData('');
  };

  const handleSign = async () => {
    if (!signatureData || !consentConfirmed) return;

    const geoLocation = await getCurrentLocation();

    const digitalSignatureData: DigitalSignatureData = {
      signatureImageUrl: signatureData,
      consentConfirmed: true,
      browserFingerprint: getBrowserFingerprint(),
      geoLocation,
    };

    onSign(digitalSignatureData);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-medium text-gray-900">
            Digital Signature - {contract.title}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {step === 'preview' && (
          <div>
            <div className="mb-6">
              <h4 className="text-lg font-medium text-gray-900 mb-4">Contract Summary</h4>
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-600">Contract Number:</span>
                  <span className="text-sm text-gray-900">{contract.contractNumber}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-600">Amount:</span>
                  <span className="text-sm text-gray-900">
                    {new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: contract.currency,
                    }).format(contract.amount)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-600">Contract Period:</span>
                  <span className="text-sm text-gray-900">
                    {new Date(contract.startDate).toLocaleDateString()} -{' '}
                    {new Date(contract.endDate).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-600">Party:</span>
                  <span className="text-sm text-gray-900">{contract.party?.name}</span>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => setStep('sign')}
                className="px-4 py-2 bg-brand-orange text-white rounded-lg text-sm font-medium hover:bg-brand-orange-dark"
              >
                Proceed to Sign
              </button>
            </div>
          </div>
        )}

        {step === 'sign' && (
          <div>
            <div className="mb-6">
              <h4 className="text-lg font-medium text-gray-900 mb-4">Create Your Signature</h4>
              <p className="text-sm text-gray-600 mb-4">
                Please draw your signature in the box below. This will be legally binding.
              </p>

              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                <canvas
                  ref={canvasRef}
                  width={600}
                  height={200}
                  className="w-full h-48 border border-gray-200 rounded cursor-crosshair"
                  onMouseDown={handleCanvasMouseDown}
                  onMouseMove={handleCanvasMouseMove}
                  onMouseUp={handleCanvasMouseUp}
                  onMouseLeave={handleCanvasMouseUp}
                />
                <div className="flex justify-between items-center mt-2">
                  <p className="text-xs text-gray-500">Sign above</p>
                  <button
                    onClick={clearSignature}
                    className="text-xs text-blue-600 hover:text-blue-800"
                  >
                    Clear Signature
                  </button>
                </div>
              </div>
            </div>

            <div className="mb-6">
              <label className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  checked={consentConfirmed}
                  onChange={e => setConsentConfirmed(e.target.checked)}
                  className="mt-1 rounded border-gray-300 text-brand-orange focus:ring-brand-orange"
                />
                <span className="text-sm text-gray-700">
                  I confirm that I have read and understood the terms of this contract, and I agree
                  to be legally bound by my digital signature. I acknowledge that this digital
                  signature has the same legal effect as a handwritten signature.
                </span>
              </label>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <div className="flex">
                <svg
                  className="w-5 h-5 text-yellow-400 mt-0.5 mr-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
                <div>
                  <h3 className="text-sm font-medium text-yellow-800">Security Information</h3>
                  <div className="mt-2 text-sm text-yellow-700">
                    <p>For security purposes, we will record:</p>
                    <ul className="list-disc list-inside mt-1 space-y-1">
                      <li>Your IP address and browser information</li>
                      <li>Timestamp of signature</li>
                      <li>Your location (if permitted)</li>
                      <li>Device fingerprint</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setStep('preview')}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Back
              </button>
              <button
                onClick={handleSign}
                disabled={!signatureData || !consentConfirmed}
                className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Sign Contract
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DigitalSignatureModal;
